import { Capacitor } from '@capacitor/core'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import { breakpoints } from '@/config/layout'

import type { ReactNode } from 'react'

type Props = {
  children: ReactNode
}

type LayoutContextData = {
  isNavigationVisible: boolean
  setIsNavigationVisible?: (visible: boolean) => void

  isWebHeaderVisible: boolean
  setIsWebHeaderVisible?: (visible: boolean) => void

  contentBackgroundColor?: string
  setContentBackgroundColor?: (color: string | undefined) => void
}

const LayoutContext = createContext<LayoutContextData>({
  isNavigationVisible: true,
  setIsNavigationVisible: undefined,
  isWebHeaderVisible: true,
  setIsWebHeaderVisible: undefined,
  contentBackgroundColor: undefined,
  setContentBackgroundColor: undefined,
})

const LayoutProvider = ({ children }: Props) => {
  const [isNavigationVisible, setIsNavigationVisible] = useState(true)
  const [isWebHeaderVisible, setIsWebHeaderVisible] = useState(true)
  const [contentBackgroundColor, setContentBackgroundColor] = useState<string | undefined>(undefined)

  return (
    <LayoutContext.Provider
      value={{
        isNavigationVisible,
        setIsNavigationVisible,
        isWebHeaderVisible,
        setIsWebHeaderVisible,
        contentBackgroundColor,
        setContentBackgroundColor,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

const useLayout = () => {
  const {
    isNavigationVisible,
    setIsNavigationVisible,
    isWebHeaderVisible,
    setIsWebHeaderVisible,
    contentBackgroundColor,
    setContentBackgroundColor,
  } = useContext(LayoutContext)

  const hideWebHeader = useCallback(() => {
    if (setIsWebHeaderVisible) {
      setIsWebHeaderVisible(false)
    }
  }, [setIsWebHeaderVisible])

  const showWebHeader = useCallback(() => {
    if (setIsWebHeaderVisible) {
      setIsWebHeaderVisible(true)
    }
  }, [setIsWebHeaderVisible])

  const hideNavigation = useCallback(() => {
    if (setIsNavigationVisible) {
      setIsNavigationVisible(false)
    }
  }, [setIsNavigationVisible])

  const showNavigation = useCallback(() => {
    if (setIsNavigationVisible) {
      setIsNavigationVisible(true)
    }
  }, [setIsNavigationVisible])

  return {
    isNavigationVisible,
    hideNavigation,
    showNavigation,
    isWebHeaderVisible,
    hideWebHeader,
    showWebHeader,
    contentBackgroundColor,
    setContentBackgroundColor,
  }
}

const useHideNativeNavigation = () => {
  const { hideNavigation, showNavigation } = useLayout()

  const isNative = Capacitor.isNativePlatform()

  // Fake this effect for dev purposes in the browser
  const isSmall = useMediaQuery(breakpoints.toS)
  const isMobile = isNative || isSmall

  useEffect(() => {
    if (isMobile) {
      hideNavigation()
    }

    return () => {
      if (isMobile) {
        showNavigation()
      }
    }
  }, [hideNavigation, isMobile, showNavigation])
}

const useHideWebHeader = () => {
  const { hideWebHeader, showWebHeader } = useLayout()

  useEffect(() => {
    hideWebHeader()

    return () => {
      showWebHeader()
    }
  }, [hideWebHeader, showWebHeader])
}

export { LayoutProvider, useHideNativeNavigation, useHideWebHeader, useLayout }
